import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Image,
  Button,
  Badge,
  Divider,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Link,
} from '@chakra-ui/react';
import {
  FaMapMarkerAlt,
  FaGlobe,
  FaGift,
  FaStore,
  FaUtensils,
  FaGamepad,
  FaPalette,
  FaCalendarAlt,
  FaTicketAlt,
  FaShoppingBag,
  FaTag,
} from 'react-icons/fa';

// Helper per gestire correttamente gli URL
const withBase = (url) => {
  if (!url) return '';
  // Se l'URL è già completo (inizia con http/https), restituiscilo così com'è
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Altrimenti, aggiungi il protocollo https se manca
  if (!url.startsWith('//')) {
    return `https://${url}`;
  }
  return `https:${url}`;
};

// Helper per ottenere l'icona in base al tipo di sconto
const getTypeIcon = (type) => {
  const typeIcons = {
    'all-events': FaCalendarAlt,
    events: FaTicketAlt,
    affiliate: FaStore,
    'all-products': FaShoppingBag,
    products: FaTag,
    feature: FaGift,
    'early-access': FaTicketAlt,
    ristorante: FaUtensils,
    negozio: FaStore,
    attivita: FaGamepad,
    cultura: FaPalette,
    servizi: FaGift,
    default: FaGift,
  };
  return typeIcons[type?.toLowerCase()] || typeIcons.default;
};

// Helper per ottenere il colore del badge in base al tipo
const getTypeColor = (type) => {
  const typeColors = {
    'all-events': 'blue',
    events: 'purple',
    affiliate: 'orange',
    'all-products': 'green',
    products: 'teal',
    feature: 'pink',
    'early-access': 'purple',
    ristorante: 'orange',
    negozio: 'blue',
    attivita: 'green',
    cultura: 'purple',
    servizi: 'teal',
    default: 'gray',
  };
  return typeColors[type?.toLowerCase()] || typeColors.default;
};

// Blocco riutilizzabile: elenco eventi (solo lista, senza titolo)
const EventiListBlock = ({ events, percentageAmount }) => {
  if (!events || events.length === 0) return null;
  return (
    <VStack spacing={3} align="stretch">
      {events.map((event) => {
        let discountedPrice = null;
        if (event.prezzo != null && percentageAmount != null) {
          const percentage = Number(String(percentageAmount).replace('%', ''));
          if (!Number.isNaN(percentage) && percentage > 0) {
            discountedPrice =
              Number(event.prezzo) - (Number(event.prezzo) * percentage) / 100;
          }
        }
        return (
          <Box
            key={event.id}
            p={3}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Text fontWeight="semibold" color="gray.800">
              {event.Nome}
            </Text>
            {event.data && (
              <Text fontSize="sm" color="blue.700" mt={1}>
                📅{' '}
                {new Date(event.data).toLocaleDateString('it-IT', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </Text>
            )}
            <Box mt={1}>
              {event.prezzo != null ? (
                <Text fontSize="sm" color="green.700">
                  💶 Prezzo:{' '}
                  <span
                    style={{
                      textDecoration: discountedPrice ? 'line-through' : 'none',
                      color: discountedPrice ? '#e73341' : undefined,
                    }}
                  >
                    <strong>
                      {Number(event.prezzo).toLocaleString('it-IT', {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </strong>
                  </span>
                  {discountedPrice != null && (
                    <>
                      {' '}
                      <span style={{ color: '#3eb297', fontWeight: 700 }}>
                        {Number(discountedPrice).toLocaleString('it-IT', {
                          style: 'currency',
                          currency: 'EUR',
                        })}
                      </span>
                      <Text fontSize="xs" color="green.600" mt={1}>
                        🎉 Prezzo scontato per soci CIL!
                      </Text>
                    </>
                  )}
                </Text>
              ) : (
                <Text fontSize="sm" color="blue.700">
                  💡 Evento gratuito per i soci CIL
                </Text>
              )}
            </Box>
            {event.description && (
              <Text fontSize="sm" color="gray.600" mt={1}>
                {event.description}
              </Text>
            )}
          </Box>
        );
      })}
    </VStack>
  );
};

// Blocco riutilizzabile: elenco prodotti (solo lista, senza titolo)
const ProdottiListBlock = ({ products }) => {
  if (!products || products.length === 0) return null;
  return (
    <VStack spacing={3} align="stretch">
      {products.map((product) => (
        <Box
          key={product.id}
          p={3}
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Text fontWeight="semibold" color="gray.800">
            {product.name}
          </Text>
          {product.description && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {product.description}
            </Text>
          )}
          {product.prezzo != null && (
            <Text fontSize="sm" color="green.700" mt={1}>
              💶{' '}
              {Number(product.prezzo).toLocaleString('it-IT', {
                style: 'currency',
                currency: 'EUR',
              })}
            </Text>
          )}
        </Box>
      ))}
    </VStack>
  );
};

// Componente per il contenuto specifico del tipo "all-events"
const AllEventsContent = ({ percentageAmount, events }) => (
  <VStack spacing={4} align="stretch">
    <Box
      bg="blue.50"
      border="2px solid"
      borderColor="blue.300"
      borderRadius="xl"
      p={5}
      textAlign="center"
    >
      <Text fontSize="3xl" fontWeight="bold" color="blue.700" mb={1}>
        −{String(percentageAmount || '').replace('%', '')}%
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="blue.800"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        su tutti gli eventi
      </Text>
      <Text fontSize="sm" color="gray.600" mt={2}>
        Lo sconto si applica in fase di prenotazione online.
      </Text>
    </Box>

    {events && events.length > 0 && (
      <Box>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
          🎪 Eventi inclusi
        </Text>
        <EventiListBlock events={events} percentageAmount={percentageAmount} />
      </Box>
    )}

    <Text fontSize="xs" color="gray.500" textAlign="center">
      💡 Si applica automaticamente in prenotazione
    </Text>
  </VStack>
);

// Componente per il contenuto specifico del tipo "events"
const EventsContent = ({ percentageAmount, events }) => (
  <VStack spacing={6} align="stretch">
    <Box>
      <Text fontSize="md" color="gray.600" lineHeight="1.6">
        Questo vantaggio ti dà diritto a uno sconto del{' '}
        <strong>{percentageAmount}%</strong> su eventi specifici selezionati da
        CIL.
      </Text>
    </Box>

    <Divider />

    <Box>
      <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
        🎪 Eventi disponibili:
      </Text>
      {events && events.length > 0 ? (
        <EventiListBlock events={events} percentageAmount={percentageAmount} />
      ) : (
        <Text color="gray.500" fontStyle="italic">
          Eventi specifici in arrivo! Controlla regolarmente per nuove
          opportunità.
        </Text>
      )}
    </Box>

    <Divider />

    <Box
      bg="purple.50"
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor="purple.200"
    >
      <Text fontSize="sm" color="purple.800" textAlign="center">
        💡 <strong>Nota:</strong> Lo sconto si applica solo agli eventi
        specifici indicati sopra.
      </Text>
    </Box>
  </VStack>
);

// Componente per il contenuto specifico del tipo "affiliate"
const AffiliateContent = ({ percentageAmount, sponsors, typeColor }) => (
  <VStack spacing={6} align="stretch">
    <Box>
      <Text fontSize="md" color="gray.600" lineHeight="1.6">
        Questo vantaggio esclusivo ti dà accesso a sconti speciali presso i
        nostri partner. Mostra la tua tessera CIL per ottenere il tuo sconto del{' '}
        <strong>{percentageAmount}%</strong>!
      </Text>
    </Box>

    <Divider />

    {/* Sponsor associati */}
    {sponsors && sponsors.length > 0 ? (
      <Box>
        <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
          Partner disponibili ({sponsors.length})
        </Text>

        <SimpleGrid columns={[1, 2]} spacing={4}>
          {sponsors.map((sponsor) => (
            <Box
              key={sponsor.id}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              p={4}
              bg="gray.50"
              _hover={{
                borderColor: `${typeColor}.400`,
                transform: 'translateY(-2px)',
                boxShadow: 'md',
              }}
              transition="all 0.3s"
            >
              <VStack spacing={3} align="stretch">
                {/* Thumbnail dello sponsor */}
                {sponsor.thumbnail && (
                  <Box textAlign="center">
                    <Image
                      src={
                        sponsor.thumbnail.formats?.small?.url ||
                        sponsor.thumbnail.url
                      }
                      alt={`Logo ${sponsor.name}`}
                      maxH="60px"
                      objectFit="contain"
                      mx="auto"
                    />
                  </Box>
                )}

                {/* Nome dello sponsor */}
                <Text fontWeight="bold" color="gray.800" textAlign="center">
                  {sponsor.name}
                </Text>

                {/* Descrizione dello sponsor */}
                {sponsor.description && (
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    textAlign="center"
                    lineHeight="1.4"
                  >
                    {sponsor.description}
                  </Text>
                )}

                {/* Indirizzo */}
                {sponsor.indirizzo && (
                  <HStack spacing={2} justify="center">
                    <Icon as={FaMapMarkerAlt} color="gray.500" />
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      {sponsor.indirizzo}
                    </Text>
                  </HStack>
                )}

                {/* Link al sito web */}
                {sponsor.urlLink && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme={typeColor}
                    leftIcon={<FaGlobe />}
                    as={Link}
                    href={withBase(sponsor.urlLink)}
                    isExternal
                    w="full"
                  >
                    Visita il sito
                  </Button>
                )}
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    ) : (
      <Box textAlign="center" py={6}>
        <Icon as={FaGift} w={12} h={12} color="gray.400" mb={3} />
        <Text color="gray.500">
          Partner in arrivo! Stiamo lavorando per aggiungere nuovi vantaggi.
        </Text>
      </Box>
    )}
  </VStack>
);

// Componente per il contenuto specifico del tipo "all-products"
const AllProductsContent = ({ percentageAmount, products }) => (
  <VStack spacing={4} align="stretch">
    <Box
      bg="green.50"
      border="2px solid"
      borderColor="green.300"
      borderRadius="xl"
      p={5}
      textAlign="center"
    >
      <Text fontSize="3xl" fontWeight="bold" color="green.700" mb={1}>
        −{String(percentageAmount || '').replace('%', '')}%
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="green.800"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        su tutto il negozio
      </Text>
      <Text fontSize="sm" color="gray.600" mt={2}>
        Lo sconto si applica al checkout su tutti i prodotti.
      </Text>
    </Box>

    {products && products.length > 0 && (
      <Box>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
          🏷️ Prodotti inclusi
        </Text>
        <ProdottiListBlock products={products} />
      </Box>
    )}

    <Text fontSize="xs" color="gray.500" textAlign="center">
      💡 Si applica automaticamente al checkout online
    </Text>
  </VStack>
);

// Componente per il contenuto specifico del tipo "products"
const ProductsContent = ({ percentageAmount, products }) => (
  <VStack spacing={6} align="stretch">
    <Box>
      <Text fontSize="md" color="gray.600" lineHeight="1.6">
        Questo vantaggio ti dà diritto a uno sconto del{' '}
        <strong>{percentageAmount}%</strong> su prodotti specifici selezionati
        da CIL.
      </Text>
    </Box>

    <Divider />

    <Box>
      <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
        🏷️ Prodotti disponibili:
      </Text>
      {products && products.length > 0 ? (
        <ProdottiListBlock products={products} />
      ) : (
        <Text color="gray.500" fontStyle="italic">
          Prodotti specifici in arrivo! Controlla regolarmente per nuove
          opportunità.
        </Text>
      )}
    </Box>

    <Divider />

    <Box
      bg="teal.50"
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor="teal.200"
    >
      <Text fontSize="sm" color="teal.800" textAlign="center">
        💡 <strong>Nota:</strong> Lo sconto si applica solo ai prodotti
        specifici indicati sopra.
      </Text>
    </Box>
  </VStack>
);

const VantaggiEsclusiviModal = ({ isOpen, onClose, sconto }) => {
  const modalBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (!sconto) return null;

  const {
    name,
    percentageAmount,
    type,
    sponsors,
    events,
    products,
    description,
  } = sconto;
  const TypeIcon = getTypeIcon(type);
  const typeColor = getTypeColor(type);

  // Funzione per renderizzare il contenuto specifico del tipo
  const renderContentByType = () => {
    switch (type) {
      case 'all-events':
        return (
          <AllEventsContent
            percentageAmount={percentageAmount}
            events={events}
          />
        );
      case 'events':
        return (
          <EventsContent percentageAmount={percentageAmount} events={events} />
        );
      case 'affiliate':
        return (
          <AffiliateContent
            percentageAmount={percentageAmount}
            sponsors={sponsors}
            typeColor={typeColor}
          />
        );
      case 'all-products':
        return (
          <AllProductsContent
            percentageAmount={percentageAmount}
            products={products}
          />
        );
      case 'products':
        return (
          <ProductsContent
            percentageAmount={percentageAmount}
            products={products}
          />
        );
      case 'feature':
        return (
          <VStack spacing={6} align="stretch">
            <Box>
              <Text
                fontSize="md"
                color="gray.600"
                lineHeight="1.6"
                whiteSpace="pre-wrap"
              >
                {description || 'Vantaggio esclusivo per i soci.'}
              </Text>
            </Box>
            {events && events.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color="gray.800"
                    mb={4}
                  >
                    🎪 Eventi inclusi:
                  </Text>
                  <EventiListBlock
                    events={events}
                    percentageAmount={percentageAmount}
                  />
                </Box>
              </>
            )}
            {products && products.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color="gray.800"
                    mb={4}
                  >
                    🏷️ Prodotti inclusi:
                  </Text>
                  <ProdottiListBlock products={products} />
                </Box>
              </>
            )}
          </VStack>
        );
      default:
        return (
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontSize="md" color="gray.600" lineHeight="1.6">
                {description ||
                  'Questo vantaggio esclusivo ti dà accesso a sconti speciali. Mostra la tua tessera CIL per ottenere il tuo sconto!'}
              </Text>
            </Box>
            {events && events.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color="gray.800"
                    mb={4}
                  >
                    🎪 Eventi inclusi:
                  </Text>
                  <EventiListBlock
                    events={events}
                    percentageAmount={percentageAmount}
                  />
                </Box>
              </>
            )}
            {products && products.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color="gray.800"
                    mb={4}
                  >
                    🏷️ Prodotti inclusi:
                  </Text>
                  <ProdottiListBlock products={products} />
                </Box>
              </>
            )}
          </VStack>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent
        bg={modalBg}
        borderRadius="xl"
        overflow="hidden"
        maxW="90vw"
        w="90vw"
      >
        <ModalHeader
          bg={`${typeColor}.50`}
          borderBottom="1px solid"
          borderColor={borderColor}
          py={6}
        >
          <HStack spacing={4}>
            <Box
              p={3}
              bg={`${typeColor}.100`}
              borderRadius="full"
              color={`${typeColor}.600`}
            >
              <Icon as={TypeIcon} w={6} h={6} />
            </Box>
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {name}
              </Text>
              <HStack spacing={2}>
                {percentageAmount && (
                  <Badge colorScheme="green" variant="outline" fontSize="sm">
                    {percentageAmount}% di sconto
                  </Badge>
                )}
              </HStack>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>{renderContentByType()}</ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VantaggiEsclusiviModal;
